import { Type, type Static } from "@sinclair/typebox";

export const EXCEL_EXPERT_AGENT_ID = "b927dd3b-11e4-4629-95ad-3b02f4c21353" as const;
export const EXCEL_EXPERT_AGENT_KEY = "excel-expert" as const;
export const EXCEL_EXPERT_DISPLAY_NAME = "Excel 专家" as const;
export const EXCEL_EXPERT_DESCRIPTION =
  "帮你查看、检查和修改 Excel 表格，生成可继续编辑的文件。" as const;
export const EXCEL_EXPERT_MATRIX_LOCALPART = "excel-expert" as const;
export const EXCEL_WORKBOOK_MEDIA_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" as const;

export const EXCEL_EXPERT_TOOL_KEYS = Object.freeze({
  open: "open_workbook",
  inspect: "inspect_workbook",
  readRange: "read_workbook_range",
  applyChangeSet: "apply_workbook_change_set",
  verify: "verify_workbook"
} as const);

const WorksheetIdSchema = Type.String({ minLength: 1, maxLength: 128 });
const A1RangeSchema = Type.String({
  minLength: 2,
  maxLength: 64,
  pattern: "^[A-Za-z]{1,3}[1-9][0-9]{0,6}(?::[A-Za-z]{1,3}[1-9][0-9]{0,6})?$"
});
const RangeTargetSchema = Type.Object(
  { worksheetId: WorksheetIdSchema, range: A1RangeSchema },
  { additionalProperties: false }
);

export const OpenWorkbookInputSchema = Type.Object(
  {
    path: Type.String({
      minLength: 7,
      maxLength: 1_024,
      pattern: "^/(?:documents|tmp/files)/[^\\u0000-\\u001f\\u007f]*\\.[xX][lL][sS][xX]$"
    })
  },
  { additionalProperties: false }
);

export const InspectWorkbookInputSchema = Type.Object({}, { additionalProperties: false });

export const ReadWorkbookRangeInputSchema = Type.Object(
  {
    worksheetId: WorksheetIdSchema,
    range: A1RangeSchema,
    includeRawValue: Type.Optional(Type.Boolean()),
    includeDisplayValue: Type.Optional(Type.Boolean()),
    includeFormula: Type.Optional(Type.Boolean()),
    includeStyle: Type.Optional(Type.Boolean()),
    maxCells: Type.Optional(Type.Integer({ minimum: 1, maximum: 100_000 })),
    maxRows: Type.Optional(Type.Integer({ minimum: 1, maximum: 10_000 })),
    maxColumns: Type.Optional(Type.Integer({ minimum: 1, maximum: 1_000 })),
    maxBytes: Type.Optional(Type.Integer({ minimum: 1_024, maximum: 8_000_000 })),
    continuation: Type.Optional(Type.String({ minLength: 1, maxLength: 512 }))
  },
  { additionalProperties: false }
);

const CellValueSchema = Type.Union([
  Type.Object({ kind: Type.Literal("blank") }, { additionalProperties: false }),
  Type.Object(
    { kind: Type.Literal("string"), value: Type.String({ maxLength: 32_767 }) },
    { additionalProperties: false }
  ),
  Type.Object(
    { kind: Type.Literal("number"), value: Type.Number() },
    { additionalProperties: false }
  ),
  Type.Object(
    { kind: Type.Literal("boolean"), value: Type.Boolean() },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      kind: Type.Literal("error"),
      value: Type.Union([
        Type.Literal("#NULL!"),
        Type.Literal("#DIV/0!"),
        Type.Literal("#VALUE!"),
        Type.Literal("#REF!"),
        Type.Literal("#NAME?"),
        Type.Literal("#NUM!"),
        Type.Literal("#N/A")
      ])
    },
    { additionalProperties: false }
  )
]);
const CellValueRowsSchema = Type.Array(
  Type.Array(CellValueSchema, { minItems: 1, maxItems: 200 }),
  { minItems: 1, maxItems: 500 }
);
const FormulaRowsSchema = Type.Array(
  Type.Array(Type.String({ minLength: 1, maxLength: 8_192, pattern: "^[^=]" }), {
    minItems: 1,
    maxItems: 200
  }),
  { minItems: 1, maxItems: 500 }
);
const ArgbSchema = Type.String({ pattern: "^[A-Fa-f0-9]{8}$" });

export const WorkbookChangeOperationSchema = Type.Union([
  Type.Object(
    { kind: Type.Literal("setValues"), target: RangeTargetSchema, rows: CellValueRowsSchema },
    { additionalProperties: false }
  ),
  Type.Object(
    { kind: Type.Literal("setFormulas"), target: RangeTargetSchema, rows: FormulaRowsSchema },
    { additionalProperties: false }
  ),
  Type.Object(
    { kind: Type.Literal("clearRange"), target: RangeTargetSchema },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      kind: Type.Literal("patchStyle"),
      target: RangeTargetSchema,
      patch: Type.Object(
        {
          bold: Type.Optional(Type.Boolean()),
          italic: Type.Optional(Type.Boolean()),
          fontArgb: Type.Optional(ArgbSchema),
          fillArgb: Type.Optional(ArgbSchema),
          numberFormat: Type.Optional(Type.String({ minLength: 1, maxLength: 256 }))
        },
        { additionalProperties: false, minProperties: 1 }
      )
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      kind: Type.Literal("setRowHeight"),
      worksheetId: WorksheetIdSchema,
      startRow: Type.Integer({ minimum: 1, maximum: 1_048_576 }),
      endRow: Type.Integer({ minimum: 1, maximum: 1_048_576 }),
      heightPoints: Type.Number({ exclusiveMinimum: 0, maximum: 409 })
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      kind: Type.Literal("setColumnWidth"),
      worksheetId: WorksheetIdSchema,
      startColumn: Type.Integer({ minimum: 1, maximum: 16_384 }),
      endColumn: Type.Integer({ minimum: 1, maximum: 16_384 }),
      width: Type.Number({ exclusiveMinimum: 0, maximum: 255 })
    },
    { additionalProperties: false }
  ),
  Type.Object(
    { kind: Type.Literal("addWorksheet"), name: Type.String({ minLength: 1, maxLength: 31 }) },
    { additionalProperties: false }
  )
]);

export const ApplyWorkbookChangeSetInputSchema = Type.Object(
  {
    operations: Type.Array(WorkbookChangeOperationSchema, { minItems: 1, maxItems: 100 })
  },
  { additionalProperties: false }
);

export const VerifyWorkbookInputSchema = Type.Object({}, { additionalProperties: false });

export type OpenWorkbookInputV1 = Static<typeof OpenWorkbookInputSchema>;
export type ReadWorkbookRangeInputV1 = Static<typeof ReadWorkbookRangeInputSchema>;
export type WorkbookChangeOperationV1 = Static<typeof WorkbookChangeOperationSchema>;
export type ApplyWorkbookChangeSetInputV1 = Static<typeof ApplyWorkbookChangeSetInputSchema>;
